/**
 * MOTOR ÚNICO DE INGRESO del ER v2 (Fase 3, en construcción).
 *
 * Reemplaza, módulo a módulo, lo que hoy hacen `usePublicidadCalcs`/`useContratosCalcs`/
 * `useBTLCalcs`/`useHardwareCalcs`/`useGestionCalcs` — cada fuente deja de aplastar
 * directamente a un `Array(12)` y pasa a producir una lista de `RegistroFinancieroER`
 * (ver `registroFinancieroER.js`). Ver docs/decisiones/2026-10-04-reingenieria-ebitda-fase3-diseno.md.
 *
 * PASO 3.2 (este): ventas (`sales`) y contratos (`sales_contratos`) — Bloque A, SOLO ingreso
 * (las 7 comisiones sobre estos mismos registros llegan en el paso 3.4). La conversión
 * LOCAL→US$ de hoy ("último TC del mes") NO se toca acá — eso sigue viviendo en
 * `estadoResultadosER`/los componentes hasta el paso 3.8 (Criterio 1). Lo único que YA es
 * definitivo desde este paso es la conversión MONEDA EXTRANJERA→LOCAL de cada documento (UF/USD
 * de una venta/contrato a CLP/PEN), que es la misma de siempre y no depende del criterio de TC
 * de display.
 *
 * Réplica deliberada (no reimplementación con criterio propio) de:
 *   - `usePublicidadCalcs.js:66-86` (`valoresMensuales`) — filtro `!opp.eliminada && opp.facturafecha`,
 *     SIN filtro de `estatus` (asimetría real con contratos, ver abajo — se preserva, no se corrige).
 *   - `useContratosCalcs.js:53-75` (`calcIngresosContratos`) — filtro `!c.eliminada && c.estatus === "100%"`.
 * Ambos usan `fechaLocalDeNegocio` (H7, `parseLocalYMD`) para ubicar el mes, y la MISMA cascada
 * de conversión extranjera→local (`getRateForOpp`/`getConvertedImporte` para ventas;
 * `c.moneda_dolar ? importe * getDolarValue(fecha) : importe` para contratos — funcionalmente
 * equivalente, los contratos siempre tienen `facturafecha` ya filtrada).
 */

import { calcularImporteNetoIntermediario, getConvertedImporte, getRateForOpp, calculateComission } from "../../utils";
import { getApxMonto } from "../../utils/apx";
import { calcularComisionEdificiosTotal, esEdificioComisionFija } from "../../utils/comisionEdificiosUtil";
import { getTakeLocal } from "../../utils/costosIntermediacion";
import { parseLocalYMD } from "../../utils/fechasFuturosPanel";
import { getImporteFacturable } from "../../utils/getImporteFacturable";
import { resolverPorcentajeBroker } from "../../utils/resolverPorcentajeBroker";
import { crearRegistroFinancieroER } from "./registroFinancieroER";

// H7 — idéntico al de usePublicidadCalcs.js/useContratosCalcs.js (mismo wrapper compartido,
// no una copia con otro nombre: 10.243).
const fechaLocalDeNegocio = (fechaStr) => parseLocalYMD(fechaStr) || new Date(NaN);

/**
 * Registros de ingreso de VENTAS (`sales`) de un (pais, ano) — réplica de
 * `usePublicidadCalcs.js:66-86`. Un registro por venta con `facturafecha` real en ese año.
 *
 * @param {object} p
 * @param {Array} p.oportunidades - Filas de `sales` del país (cualquier año; se filtra adentro).
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} p.getDolarValue - `(fechaStr) => number`, YA bound al país (como hoy).
 * @param {number} p.dolarFallbackPais - Fallback de TC del país (como hoy, `dolarFallback[pais]`).
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosVentas = ({ oportunidades, pais, ano, getDolarValue, dolarFallbackPais }) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha)
    .forEach((opp) => {
      const fecha = fechaLocalDeNegocio(opp.facturafecha);
      if (isNaN(fecha.getTime())) return;
      const mesIndice = fecha.getMonth();
      const facturaYear = fecha.getFullYear();
      if (facturaYear !== ano) return;

      const montoLocal = getConvertedImporte(
        { ...opp, importe: getImporteFacturable(opp) },
        getRateForOpp(opp, getDolarValue, dolarFallbackPais)
      );

      registros.push(
        crearRegistroFinancieroER({
          fecha: String(opp.facturafecha).slice(0, 10),
          ano,
          mesIndice,
          montoLocal,
          bloque: "A",
          concepto: "ingreso_ventas",
          pais,
          meta: { id: opp.id, origen: "sales" },
        })
      );
    });
  return registros;
};

/**
 * Registros de ingreso de CONTRATOS (`sales_contratos`) de un (pais, ano) — réplica de
 * `useContratosCalcs.js:53-75` (`calcIngresosContratos`). Un registro por cada línea de
 * `meses[]` con `facturafecha` real en ese año (ER solo cuenta lo realmente facturado).
 *
 * @param {object} p
 * @param {Array} p.contratos - Filas de `sales_contratos` del país (cualquier año).
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} p.getDolarValue - `(fechaStr) => number`, YA bound al país.
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosContratos = ({ contratos, pais, ano, getDolarValue }) => {
  const registros = [];
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        if (!mesObj.facturafecha) return; // ER: solo facturado real, nunca proyectado.
        const fecha = fechaLocalDeNegocio(mesObj.facturafecha);
        if (isNaN(fecha.getTime())) return;
        const mesIndice = fecha.getMonth();
        const year = fecha.getFullYear();
        if (year !== ano) return;

        const importe = getImporteFacturable({
          importe: mesObj.importe,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
        });
        const montoLocal = c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;

        registros.push(
          crearRegistroFinancieroER({
            fecha: String(mesObj.facturafecha).slice(0, 10),
            ano,
            mesIndice,
            montoLocal,
            bloque: "A",
            concepto: "ingreso_contratos",
            pais,
            meta: { id: c.id, mes: mesObj.mes, origen: "sales_contratos" },
          })
        );
      });
    });
  return registros;
};

/**
 * Azúcar: ventas + contratos de un (pais, ano) — el ingreso completo del Bloque A
 * ("FACTURACIÓN A", `usePublicidadCalcs`+`useContratosCalcs` combinados, sección 2.1 del diseño).
 */
export const registrosIngresoA = (params) => [...registrosVentas(params), ...registrosContratos(params)];

/**
 * PASO 3.3: BTL (`sales_btl`), Hardware y Gestión (`negocios_carteleria`) — Bloques D/B/C,
 * SOLO ingreso. P2.117-1 (BTL sin chequeo `moneda_dolar`) NO se corrige acá — se replica tal
 * cual, con su delta pendiente para un paso de corrección posterior (3.6b o después).
 */

/**
 * Registros de ingreso de BTL (`sales_btl`) de un (pais, ano) — réplica de
 * `useBTLCalcs.js:26-36` (`calcIngresosBTL`). **P2.117-1, NO corregido acá**: no hay chequeo de
 * `moneda_dolar` — `row.total_investment` se toma siempre como moneda LOCAL, aunque la fila
 * esté en USD (bug real preexistente, ya catalogado en el ROADMAP, se replica tal cual).
 */
export const registrosBTL = ({ salesBTL, pais, ano }) => {
  const registros = [];
  (salesBTL || []).forEach((row) => {
    if (!row.facturafecha) return;
    const fecha = fechaLocalDeNegocio(row.facturafecha);
    if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return;
    const mesIndice = fecha.getMonth();
    const montoLocal = Number(row.total_investment) || 0;
    registros.push(
      crearRegistroFinancieroER({
        fecha: String(row.facturafecha).slice(0, 10),
        ano,
        mesIndice,
        montoLocal,
        bloque: "D",
        concepto: "ingreso_btl",
        pais,
        meta: { id: row.id, origen: "sales_btl" },
      })
    );
  });
  return registros;
};

/**
 * Registros de ingreso de HARDWARE (`negocios_carteleria`) de un (pais, ano) — réplica de
 * `useHardwareCalcs.js:54-215` (`calcIngresosHardware`), un registro por cada tramo/cuota que
 * el hook viejo suma a `mapa[mes]`. **P2.117-2, NO corregido acá** (fallback CAPEX-sin-desglose
 * usa fin de mes para la conversión, no el día 1 de la factory compartida — se replica tal cual).
 *
 * `fecha` del registro (para el futuro Criterio 1, sección 6 riesgo 3 del diseño — NO consumido
 * todavía): la fecha REAL del tramo/factura cuando existe (desglose, otros-CAPEX, OPEX facturado
 * real, OPEX-por-cuota con match en `costosGestion`); `null` ("sin fecha propia", decisión 2)
 * cuando el monto es una proyección/estimación sin documento real (CAPEX sin desglose, OPEX por
 * cuota sin match) — el mes/año de ubicación en la tabla NO cambia por esto, solo el futuro TC.
 * La conversión EXTRANJERA→LOCAL (UF/USD del tramo) se calcula AHORA, con la MISMA fecha que usa
 * el hook viejo para ese fin (fin de mes en los casos sin documento real) — eso no cambia.
 *
 * @param {object} p
 * @param {Array} p.negocios - Filas de `negocios_carteleria` del país.
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} p.getUFValue - `(fechaStr) => number` (solo aplica si pais==="Chile").
 * @param {Function} p.getDolarValue - `(fechaStr) => number`, YA bound al país.
 * @param {Array|null} [p.costosGestion] - `costos_gestion_contenidos`, para la fecha real de
 *   factura del camino OPEX-por-cuota (null = sin esa tabla, igual que año anterior/Perú hoy).
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosHardware = ({ negocios, pais, ano, getUFValue, getDolarValue, costosGestion = null }) => {
  const registros = [];
  const defaultMoneda = pais === "Chile" ? "UF" : "PEN";

  (negocios || []).forEach((n) => {
    if (!n.incluye_hardware || n.eliminada) return;
    if (!["100%", "Activo"].includes(n.estado)) return;
    const moneda = (n.moneda_hardware || defaultMoneda).toUpperCase();
    let yaProcesadoConDesglose = false;

    const convertir = (monto, fechaParaTC) => {
      if (moneda === "UF" && pais === "Chile") return Math.round(monto * getUFValue(fechaParaTC));
      if (moneda === "US$" || moneda === "USD") return Math.round(monto * getDolarValue(fechaParaTC));
      return monto;
    };

    const push = (fecha, mesIndice, montoLocal, tramo) => {
      registros.push(
        crearRegistroFinancieroER({
          fecha,
          ano,
          mesIndice,
          montoLocal,
          bloque: "B",
          concepto: "ingreso_hardware",
          pais,
          meta: { id: n.id, origen: "negocios_carteleria", tramo },
        })
      );
    };

    // CAPEX con desglose de pagos (tramos reales, 1 registro por tramo facturado).
    if (
      n.modalidad === "CAPEX" &&
      Array.isArray(n.detalle_pagos_hardware) &&
      n.detalle_pagos_hardware.some((p) => p.monto != null)
    ) {
      n.detalle_pagos_hardware.forEach((pago) => {
        const fechaTramo = pago.facturafecha || pago.fecha_factura;
        if (!fechaTramo || pago.monto == null) return;
        const fecha = fechaLocalDeNegocio(fechaTramo);
        const mes = fecha.getMonth();
        const anoTramo = fecha.getFullYear();
        if (anoTramo !== ano) return;
        const fechaUsada = String(fechaTramo).split("T")[0];
        push(fechaUsada, mes, convertir(Number(pago.monto), fechaUsada), "capex_desglose");
      });
      yaProcesadoConDesglose = true;
    }

    // OTROS CAPEX (mismo patrón, tabla separada).
    if (n.modalidad === "CAPEX" && Array.isArray(n.detalle_otros_hardware) && n.detalle_otros_hardware.length > 0) {
      n.detalle_otros_hardware.forEach((pago) => {
        if (!pago.fecha_factura || pago.monto == null) return;
        const fecha = fechaLocalDeNegocio(pago.fecha_factura);
        const mes = fecha.getMonth();
        const anoTramo = fecha.getFullYear();
        if (anoTramo !== ano) return;
        const fechaUsada = String(pago.fecha_factura).split("T")[0];
        push(fechaUsada, mes, convertir(Number(pago.monto), fechaUsada), "capex_otros");
      });
    }

    // CAPEX sin desglose (fallback) / OPEX — mutuamente exclusivos, igual que el hook viejo.
    if (n.modalidad === "CAPEX" && n.fecha_cierre && !yaProcesadoConDesglose) {
      const fechaCierre = fechaLocalDeNegocio(n.fecha_cierre);
      const mesCierre = fechaCierre.getMonth();
      const anoCierre = fechaCierre.getFullYear();
      if (anoCierre !== ano) return;
      const ultimoDia = new Date(ano, mesCierre + 1, 0);
      const fechaStr = ultimoDia.toISOString().split("T")[0];
      const montoLocal = convertir(Number(n.monto_hardware) || 0, fechaStr);
      // Sin tramo real: "sin fecha propia" (decisión 2) — el mes es el de fecha_cierre; la
      // conversión (ya hecha arriba con fin de mes, P2.117-2) no cambia.
      push(null, mesCierre, montoLocal, "capex_sin_desglose");
    } else if (n.modalidad === "OPEX" && n.cuota_hardware_opex && n.fecha_cierre) {
      const pantallas = Number(n.cantidad_pantallas) || 1;
      const cuotaBase = Number(n.cuota_hardware_opex);

      // Modo "facturado real": meses_facturacion con entries tipo "opex".
      const mesesFact = Array.isArray(n.meses_facturacion) ? n.meses_facturacion : [];
      const opexEntries = mesesFact.filter((e) => e && e.tipo === "opex" && e.facturafecha);
      if (opexEntries.length > 0) {
        opexEntries.forEach((e) => {
          // Parseo manual (NO fechaLocalDeNegocio) — réplica deliberada, ver Anexo A del diseño.
          const [fy, fm] = e.facturafecha.split("-").map(Number);
          if (fy !== ano) return;
          const idx = fm - 1;
          if (idx < 0 || idx > 11) return;
          const montoLocal = convertir(cuotaBase * pantallas, e.facturafecha);
          push(String(e.facturafecha).slice(0, 10), idx, montoLocal, "opex_facturado_real");
        });
        return;
      }

      // Fallback por cuota: itera desde fecha_cierre, cruzando años si corresponde.
      const fechaCierre = fechaLocalDeNegocio(n.fecha_cierre);
      const mesCierreOpex = fechaCierre.getMonth();
      const anoCierre = fechaCierre.getFullYear();
      const totalCuotas = Number(n.num_cuotas) || Number(n.plazo_contrato_meses) || 36;

      let cuotaActual = 0;
      let anoActual = anoCierre;
      let mesActual = mesCierreOpex;

      while (cuotaActual < totalCuotas) {
        if (anoActual === ano && mesActual >= 0 && mesActual < 12) {
          let fechaPropia = null;
          let fechaParaTC;
          if (costosGestion) {
            const registro = costosGestion.find(
              (c) =>
                Number(c.negocio_id) === n.id &&
                Number(c.mes) === mesActual + 1 &&
                Number(c.ano) === ano &&
                c.fecha_factura
            );
            const ultimoDia = new Date(ano, mesActual + 1, 0);
            fechaParaTC = registro?.fecha_factura?.split("T")[0] || ultimoDia.toISOString().split("T")[0];
            fechaPropia = registro?.fecha_factura ? fechaParaTC : null;
          } else {
            const ultimoDia = new Date(ano, mesActual + 1, 0);
            fechaParaTC = ultimoDia.toISOString().split("T")[0];
          }
          const montoLocal = convertir(cuotaBase * pantallas, fechaParaTC);
          push(fechaPropia, mesActual, montoLocal, "opex_por_cuota");
        }
        cuotaActual++;
        mesActual++;
        if (mesActual >= 12) {
          mesActual = 0;
          anoActual++;
        }
        if (anoActual > ano + 1) break;
      }
    }
  });
  return registros;
};

// ─── Gestión: helpers privados, réplica exacta de useGestionCalcs.js (10.243) ──────────────

// `useGestionCalcs.js:40-77` — null si el negocio no tiene `meses_facturacion` poblado.
const getCuotasFacturadasPorMes = (negocio, year) => {
  const entries = Array.isArray(negocio.meses_facturacion) ? negocio.meses_facturacion : [];
  if (entries.length === 0) return null;
  const incluyeContenidos = !!negocio.incluye_contenidos;
  const result = Array.from({ length: 12 }, () => ({ gestion: 0, contenidos: 0, fechaFactura: null }));
  let hasAny = false;
  entries.forEach((e) => {
    if (!e || !e.facturafecha) return;
    if (e.tipo !== "gestion" && e.tipo !== "contenidos") return;
    const [fy, fm] = e.facturafecha.split("-").map(Number);
    if (fy !== year) return;
    const idx = fm - 1;
    if (idx < 0 || idx > 11) return;
    result[idx][e.tipo] += 1;
    result[idx].fechaFactura = e.facturafecha;
    hasAny = true;
  });
  if (incluyeContenidos) {
    const contInicio = negocio.contenidos_fecha_inicio ? new Date(negocio.contenidos_fecha_inicio + "T00:00:00") : null;
    const contFin = negocio.contenidos_fecha_fin ? new Date(negocio.contenidos_fecha_fin + "T00:00:00") : null;
    result.forEach((slot, idx) => {
      if (slot.gestion > 0 && slot.contenidos === 0) {
        const slotDate = new Date(year, idx, 1);
        const enRango = (!contInicio || slotDate >= contInicio) && (!contFin || slotDate <= contFin);
        if (enRango) slot.contenidos = slot.gestion;
      }
    });
  }
  return hasAny ? result : null;
};

// `useGestionCalcs.js:115-120`.
const contenidosVigenteEnMes = (negocio, year, monthIndex) => {
  const contInicio = negocio.contenidos_fecha_inicio ? new Date(negocio.contenidos_fecha_inicio + "T00:00:00") : null;
  const contFin = negocio.contenidos_fecha_fin ? new Date(negocio.contenidos_fecha_fin + "T00:00:00") : null;
  const slotDate = new Date(year, monthIndex, 1);
  return (!contInicio || slotDate >= contInicio) && (!contFin || slotDate <= contFin);
};

/**
 * Registros de ingreso de GESTIÓN & CONTENIDOS (`negocios_carteleria`) de un (pais, ano) —
 * réplica de las variantes PAÍS-ESPECÍFICAS `useGestionCalcs.js:485-627`
 * (`ingresosGestionContenidosMensualChile`/`...Peru`), NO de la variante "genérica"
 * (`ingresosGestionContenidosMensual`, líneas 311-401): esa variante hace, SOLO en su camino
 * fallback, una búsqueda adicional de `fecha_factura` real en `costos_gestion_contenidos` que
 * las variantes país-específicas NO hacen (usan directamente fin de mes) — una 3ª asimetría de
 * TC, no catalogada en P2.117, encontrada al escribir este paso. Se eligen las variantes
 * país-específicas porque son las que alimentan `gestionMensualesCombinados` (la única agregación
 * cross-country hoy) y porque son simétricas entre sí — NO se corrige acá, se documenta en el
 * cierre de este paso para una futura corrección.
 *
 * Perú usa el mecanismo `toPEN` (TC MENSUAL `dolarMensualPeru[mes]`, sin granularidad de día —
 * P2.117-3, NO corregido acá); Chile usa `convertirAMonedaLocal` (factory bound a "Chile", TC de
 * la fecha puntual real o fin de mes en el fallback).
 *
 * @param {object} p
 * @param {Array} p.negocios - Filas de `negocios_carteleria` del país.
 * @param {"Chile"|"Perú"} p.pais
 * @param {number} p.ano
 * @param {Function} [p.convertirAMonedaLocal] - Factory bound al país (solo si pais==="Chile").
 * @param {Array} [p.dolarMensualPeru] - Array(12) (solo si pais==="Perú").
 * @param {object} [p.dolarFallback] - `{ Chile, Perú }` (solo si pais==="Perú").
 * @returns {Array} RegistroFinancieroER[]
 */
export const registrosGestion = ({ negocios, pais, ano, convertirAMonedaLocal, dolarMensualPeru, dolarFallback }) => {
  const registros = [];

  const toPEN = (monto, moneda, mesIndice) => {
    if (!monto) return 0;
    const m = (moneda || "PEN").toUpperCase();
    if (m === "USD" || m === "US$") return monto * (dolarMensualPeru[mesIndice] || dolarFallback["Perú"]);
    return monto;
  };

  (negocios || []).forEach((negocio) => {
    if (negocio.eliminada || negocio.estado !== "Activo" || !negocio.fecha_inicio) return;
    const fechaInicio = fechaLocalDeNegocio(negocio.fecha_inicio);
    const plazoMeses = parseInt(negocio.plazo_contrato_meses) || 0;
    if (plazoMeses === 0) return;
    const pantallas = parseInt(negocio.cantidad_pantallas) || 1;
    const gestionUnitario = parseFloat(negocio.fee_gestion_unitario) || 0;
    const contenidosUnitario = negocio.incluye_contenidos ? parseFloat(negocio.fee_contenidos_unitario) || 0 : 0;

    const montoGestion = (monto, moneda, mes1, fechaOverride) =>
      pais === "Perú"
        ? toPEN(monto, moneda, mes1 - 1)
        : convertirAMonedaLocal(monto, moneda, pais, negocio.id, mes1, ano, fechaOverride);

    const push = (fecha, mesIndice, montoLocal, concepto) => {
      registros.push(
        crearRegistroFinancieroER({
          fecha,
          ano,
          mesIndice,
          montoLocal,
          bloque: "C",
          concepto,
          pais,
          meta: { id: negocio.id, origen: "negocios_carteleria" },
        })
      );
    };

    const cuotasFacturadas = getCuotasFacturadasPorMes(negocio, ano);
    if (cuotasFacturadas) {
      cuotasFacturadas.forEach((slot, m) => {
        if (slot.gestion === 0 && slot.contenidos === 0) return;
        if (slot.gestion > 0 && gestionUnitario > 0) {
          const monto = montoGestion(
            gestionUnitario * pantallas * slot.gestion,
            negocio.moneda_gestion,
            m + 1,
            slot.fechaFactura
          );
          push(slot.fechaFactura, m, monto, "ingreso_gestion");
        }
        if (slot.contenidos > 0 && contenidosUnitario > 0) {
          const monedaContenidos =
            pais === "Perú" ? negocio.moneda_contenidos || negocio.moneda_gestion : negocio.moneda_contenidos;
          const monto = montoGestion(
            contenidosUnitario * pantallas * slot.contenidos,
            monedaContenidos,
            m + 1,
            slot.fechaFactura
          );
          push(slot.fechaFactura, m, monto, "ingreso_contenidos");
        }
      });
      return;
    }

    for (let m = 0; m < 12; m++) {
      const ultimoDiaMes = new Date(ano, m + 1, 0);
      if (fechaInicio > ultimoDiaMes) continue;
      const fechaParaUF = ultimoDiaMes.toISOString().split("T")[0];
      if (gestionUnitario > 0) {
        const monto = montoGestion(gestionUnitario * pantallas, negocio.moneda_gestion, m + 1, fechaParaUF);
        // Sin factura real (camino fallback): "sin fecha propia" (decisión 2) — el mes es m.
        push(null, m, monto, "ingreso_gestion");
      }
      if (contenidosUnitario > 0 && contenidosVigenteEnMes(negocio, ano, m)) {
        const monedaContenidos =
          pais === "Perú" ? negocio.moneda_contenidos || negocio.moneda_gestion : negocio.moneda_contenidos;
        const monto = montoGestion(contenidosUnitario * pantallas, monedaContenidos, m + 1, fechaParaUF);
        push(null, m, monto, "ingreso_contenidos");
      }
    }
  });
  return registros;
};

/**
 * PASO 3.4: las 6 comisiones sobre los registros de ingreso (agencia, edificios, broker, take,
 * APX, comercial) — INVOCANDO `calculateComission`/`comisionEdificiosUtil`/`apx.js`/
 * `costosIntermediacion.js` SIN CAMBIOS, réplica deliberada de los memos standalone de
 * `usePublicidadCalcs.js`/`useContratosCalcs.js` (NO de lo que corre internamente dentro de
 * `calculateSalesCommission` para la comisión comercial — ese es un camino de código aparte que
 * YA existe hoy, duplicado a propósito, y este paso no lo toca ni lo unifica).
 *
 * Signo: estos registros guardan la MAGNITUD POSITIVA de cada comisión, igual que
 * `pubAgencia`/`pubEdificios`/etc. hoy (`valores[mes] += monto`, nunca negativo) y que
 * `costosER.js` (costos en positivo, la resta ocurre en la fórmula del EBITDA, no en el signo
 * del registro) — la reconciliación con la convención "−costo" del tipo `RegistroFinancieroER`
 * del diseño (§1.1) se resuelve en `estadoResultadosER` (paso 3.6), no acá: cambiar el signo acá
 * sin cambiar el consumidor rompería la paridad que este paso tiene que probar.
 *
 * Cada función tiene su PROPIO filtro (no reusa el de `registrosVentas`/`registrosContratos`):
 * agencia/broker/take/apx filtran `!opp.eliminada && opp.facturafecha && opp.client_id`;
 * edificios/comercial filtran `opp.estatus === "100%" && !opp.eliminada && opp.facturafecha`
 * (+`!opp.isBTL` en edificios) — asimetría real con el filtro de ingreso (sin `estatus` ni
 * `client_id`), preservada tal cual.
 */

// ─── VENTAS (`sales`) ───────────────────────────────────────────────────────────────────────

// Convierte una opp con `moneda_dolar` a moneda local — réplica de `oppEnLocal` (usePublicidadCalcs.js:234-238).
const oppEnLocal = (opp, getDolarValue, dolarFallbackPais) => {
  if (!opp.moneda_dolar) return opp;
  const rate = getRateForOpp(opp, getDolarValue, dolarFallbackPais);
  return { ...opp, importe: (parseFloat(opp.importe) || 0) * rate, moneda_dolar: false };
};

const fechaDeVentaValida = (opp, ano) => {
  if (!opp.facturafecha) return null;
  const fecha = fechaLocalDeNegocio(opp.facturafecha);
  if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return null;
  return fecha;
};

const pushComision = (registros, { fecha, ano, mesIndice, montoLocal, bloque, concepto, pais, meta }) => {
  if (!montoLocal) return;
  registros.push(crearRegistroFinancieroER({ fecha, ano, mesIndice, montoLocal, bloque, concepto, pais, meta }));
};

/** Réplica de `valoresMensualesComisionAgencia` (`usePublicidadCalcs.js:240-261`). */
export const registrosComisionAgenciaVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const neto = calcularImporteNetoIntermediario(
        local,
        Object.values(clientes),
        Object.values(intermediarios),
        pais,
        pctMap
      );
      const montoLocal = getImporteFacturable(local) - neto;
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_agencia",
        pais,
        meta: { id: opp.id, origen: "sales" },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesComisionBroker` (`usePublicidadCalcs.js:362-388`). */
export const registrosComisionBrokerVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const importeLocal = parseFloat(local.importe) || 0;
      const pct = resolverPorcentajeBroker(opp, clientesArr, intermArr, fecha, pctMap);
      const montoLocal = (importeLocal * pct) / 100;
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_broker",
        pais,
        meta: { id: opp.id, origen: "sales" },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesTake` (`usePublicidadCalcs.js:621-634`). SIN equivalente en contratos. */
export const registrosComisionTakeVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  costosMap,
}) => {
  const registros = [];
  if (!costosMap) return registros;
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const montoLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallbackPais);
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "take",
        pais,
        meta: { id: opp.id, origen: "sales" },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesApx` (`usePublicidadCalcs.js:736-752`, mismo patrón de `apxPorMes`). */
export const registrosComisionApxVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => !opp.eliminada && opp.facturafecha && opp.client_id)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const montoLocal = getApxMonto(local, intermediarios, pctMap);
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "apx",
        pais,
        meta: { id: opp.id, origen: "sales" },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesComisiones` (edificios, `usePublicidadCalcs.js:803-832`). */
export const registrosComisionEdificiosVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  edificios,
  snapMap = null,
  costosMap,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha && !opp.isBTL)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const netoFactura = calcularImporteNetoIntermediario(
        local,
        Object.values(clientes),
        Object.values(intermediarios),
        pais,
        pctMap
      );
      const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallbackPais);
      const apxLocal = getApxMonto(local, intermediarios, pctMap);
      const montoLocal = calcularComisionEdificiosTotal(local, edificios, netoFactura, snapMap, takeLocal, apxLocal);
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_edificios",
        pais,
        meta: { id: opp.id, origen: "sales" },
      });
    });
  return registros;
};

/** Réplica de `valoresMensualesComisionComercial.valores` (`usePublicidadCalcs.js:1059-1158`). */
export const registrosComisionComercialVentas = ({
  oportunidades,
  pais,
  ano,
  getDolarValue,
  dolarFallbackPais,
  clientes,
  intermediarios,
  edificios,
  snapMap = null,
  costosMap,
  pctMap = null,
}) => {
  const registros = [];
  (oportunidades || [])
    .filter((opp) => opp.estatus === "100%" && !opp.eliminada && opp.facturafecha)
    .forEach((opp) => {
      const fecha = fechaDeVentaValida(opp, ano);
      if (!fecha) return;
      const local = oppEnLocal(opp, getDolarValue, dolarFallbackPais);
      const takeLocal = getTakeLocal(opp, "sales", opp.id, costosMap, getDolarValue, dolarFallbackPais);
      const montoLocal = calculateComission.calculateSalesCommission(
        local,
        clientes,
        intermediarios,
        edificios,
        undefined,
        snapMap,
        takeLocal,
        pctMap
      );
      pushComision(registros, {
        fecha: String(opp.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "A",
        concepto: "comision_comercial",
        pais,
        meta: { id: opp.id, origen: "sales" },
      });
    });
  return registros;
};

// ─── CONTRATOS (`sales_contratos`) ─────────────────────────────────────────────────────────

const mesContratoValido = (c, mesObj, ano) => {
  if (!mesObj.facturafecha) return null;
  const fecha = fechaLocalDeNegocio(mesObj.facturafecha);
  if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return null;
  return fecha;
};

const importeLocalContrato = (c, mesObj, getDolarValue) => {
  const importe = Number(mesObj.importe) || 0;
  return c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;
};

/** Réplica de `calcComisionAgenciaContratos` (`useContratosCalcs.js:160-191`). */
export const registrosComisionAgenciaContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const oppObj = {
          id: `contrato-${c.id}-${mesObj.mes}`,
          importe: importeLocal,
          client_id: c.client_id,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          facturafecha: mesObj.facturafecha,
          isBTL: false,
        };
        const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermArr, pais, pctMap);
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal: importeLocal - neto,
          bloque: "A",
          concepto: "comision_agencia",
          pais,
          meta: { id: c.id, mes: mesObj.mes, origen: "sales_contratos" },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionBrokerContratos` (`useContratosCalcs.js:196-227`). */
export const registrosComisionBrokerContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  clientes,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const pct = resolverPorcentajeBroker(
          { id: `contrato-${c.id}-${mesObj.mes}`, client_id: c.client_id, pais },
          clientesArr,
          intermArr,
          fecha,
          pctMap
        );
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal: (importeLocal * pct) / 100,
          bloque: "A",
          concepto: "comision_broker",
          pais,
          meta: { id: c.id, mes: mesObj.mes, origen: "sales_contratos" },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionApxContratos` (`useContratosCalcs.js:357-379`). */
export const registrosComisionApxContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  intermediarios,
  pctMap = null,
}) => {
  const registros = [];
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%" && c.apx_id != null)
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const montoLocal = getApxMonto(
          { id: `contrato-${c.id}-${mesObj.mes}`, apx_id: c.apx_id, importe: importeLocal },
          intermArr,
          pctMap
        );
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal,
          bloque: "A",
          concepto: "apx",
          pais,
          meta: { id: c.id, mes: mesObj.mes, origen: "sales_contratos" },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionEdificiosContratos` (`useContratosCalcs.js:109-157`). take=0 (pendiente FASE 6). */
export const registrosComisionEdificiosContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  edificios,
  clientes,
  intermediarios,
  snapMap = null,
  pctMap = null,
}) => {
  const registros = [];
  if (!edificios || Object.keys(edificios).length === 0) return registros;
  const clientesArr = Object.values(clientes);
  const intermArr = Object.values(intermediarios);
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        if (!mesObj.edificios_ids) return;
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const oppObj = {
          id: `contrato-${c.id}-${mesObj.mes}`,
          edificiosparticipantes: mesObj.edificios_ids,
          pantallastotales: mesObj.pantallas_totales || 0,
          pantallas_ascensores: mesObj.pantallas_ascensores || "",
          pantallas_espera: mesObj.pantallas_espera || "",
          pantallas_circulacion: mesObj.pantallas_circulacion || "",
          pantallas_cowork: mesObj.pantallas_cowork || "",
          facturafecha: mesObj.facturafecha,
          importe: importeLocal,
          client_id: c.client_id,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          apx_id: c.apx_id,
          isBTL: false,
        };
        const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermArr, pais, pctMap);
        const apxLocal = getApxMonto(oppObj, intermArr, pctMap);
        const montoLocal = calcularComisionEdificiosTotal(oppObj, edificios, neto, snapMap, 0, apxLocal);
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal,
          bloque: "A",
          concepto: "comision_edificios",
          pais,
          meta: { id: c.id, mes: mesObj.mes, origen: "sales_contratos" },
        });
      });
    });
  return registros;
};

/** Réplica de `calcComisionComercialContratos` (`useContratosCalcs.js:425-479`). take=0 (pendiente FASE 6). */
export const registrosComisionComercialContratos = ({
  contratos,
  pais,
  ano,
  getDolarValue,
  edificios,
  clientes,
  intermediarios,
  snapMap = null,
  pctMap = null,
}) => {
  const registros = [];
  (contratos || [])
    .filter((c) => !c.eliminada && c.estatus === "100%")
    .forEach((c) => {
      (c.meses || []).forEach((mesObj) => {
        const fecha = mesContratoValido(c, mesObj, ano);
        if (!fecha) return;
        const importeLocal = importeLocalContrato(c, mesObj, getDolarValue);
        const oppObj = {
          id: `contrato-${c.id}-${mesObj.mes}`,
          importe: importeLocal,
          client_id: c.client_id,
          descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          apx_id: c.apx_id,
          pais,
          facturafecha: mesObj.facturafecha,
          ejecutivo: c.ejecutivo,
          mes_cierre: c.mes_cierre,
          moneda_dolar: false,
          isBTL: false,
          edificiosparticipantes: mesObj.edificios_ids || "",
          pantallastotales: mesObj.pantallas_totales || 0,
          pantallas_ascensores: mesObj.pantallas_ascensores || "",
          pantallas_espera: mesObj.pantallas_espera || "",
          pantallas_circulacion: mesObj.pantallas_circulacion || "",
          pantallas_cowork: mesObj.pantallas_cowork || "",
        };
        const montoLocal = calculateComission.calculateSalesCommission(
          oppObj,
          clientes,
          intermediarios,
          edificios,
          undefined,
          snapMap,
          0,
          pctMap
        );
        pushComision(registros, {
          fecha: String(mesObj.facturafecha).slice(0, 10),
          ano,
          mesIndice: fecha.getMonth(),
          montoLocal,
          bloque: "A",
          concepto: "comision_comercial",
          pais,
          meta: { id: c.id, mes: mesObj.mes, origen: "sales_contratos" },
        });
      });
    });
  return registros;
};

// ─── PASO 3 (Criterio 1), punto 2 (F1) — los 2 gaps reales de registro de comisión ──────────
// De las 13 series de comisión, 11 YA tenían función de registro exacta (3.1-3.5). Faltaban
// estas 2 — ninguna necesita aproximación, ambas tienen monto por mes/venta ya resuelto hoy.

/**
 * Comisión FIJA de edificios (Bloque A) — réplica de `calcularComisionesFijasMensuales`
 * (`comisionEdificiosUtil.js:319-333`): para cada mes, suma `comision_fija_monto` de los
 * edificios con comisión fija vigente ese mes (convertido de USD a local con el TC del día 1
 * del mes, igual que la función original — ESE monto en pesos NO cambia, se replica tal cual).
 *
 * `fecha: null` a propósito (punto 2 del encargo): la comisión fija no nace de un documento con
 * fecha propia — es una condición de vigencia evaluada mes a mes (como RRHH/Depreciación), así
 * que su US$ usa el TC promedio real del mes (Criterio 1b), no el TC del día 1 que usa el
 * cálculo en pesos (ese TC del día 1 solo convierte USD de catálogo→CLP/PEN, no es una fecha de
 * documento — son dos conversiones distintas, con dos propósitos distintos).
 *
 * Un registro por (edificio, mes) en vez de un total mensual — igual granularidad que el resto
 * de las 11 funciones de comisión (un registro por hecho, nunca un total pre-sumado).
 */
export const registrosComisionEdificiosFijas = ({ edificios, pais, ano, getDolarValue }) => {
  const registros = [];
  const edificiosMap = edificios || {};
  const listaEdificios = Object.entries(edificiosMap);
  for (let mes = 0; mes < 12; mes++) {
    const fechaPrimero = `${ano}-${String(mes + 1).padStart(2, "0")}-01`;
    listaEdificios.forEach(([id, ed]) => {
      if (!esEdificioComisionFija(ed, fechaPrimero)) return;
      const monto = Number(ed.comision_fija_monto) || 0;
      if (monto <= 0) return;
      const moneda = (ed.comision_fija_moneda || "USD").toUpperCase();
      const montoLocal = moneda === "USD" ? monto * getDolarValue(fechaPrimero) : monto;
      pushComision(registros, {
        fecha: null,
        ano,
        mesIndice: mes,
        montoLocal,
        bloque: "A",
        concepto: "comision_edificios",
        pais,
        meta: { id, origen: "edificios_comision_fija" },
      });
    });
  }
  return registros;
};

/**
 * Comisión de edificios de BTL (Bloque D) — réplica de `useBTLCalcs.calcComisionEdificiosBTL`
 * (`useBTLCalcs.js:38-53`): por cada venta BTL con `buildings` (jsonb), suma
 * `importe * (comision/100)` de cada edificio participante.
 *
 * `fecha` SÍ disponible (a diferencia de la fija): es la fecha de la venta BTL
 * (`row.facturafecha`), el MISMO documento/fecha que ya usa `registrosBTL` (el ingreso del
 * mismo bloque D) — Criterio 1a, TC de esa fecha exacta.
 */
export const registrosComisionEdificiosBTL = ({ salesBTL, pais, ano }) => {
  const registros = [];
  (salesBTL || []).forEach((row) => {
    if (!row.facturafecha || !row.buildings) return;
    const fecha = fechaLocalDeNegocio(row.facturafecha);
    if (isNaN(fecha.getTime()) || fecha.getFullYear() !== ano) return;
    const buildings = typeof row.buildings === "string" ? JSON.parse(row.buildings) : row.buildings;
    (buildings || []).forEach((building, idx) => {
      const importe = Number(building.importe) || 0;
      const porcentaje = Number(building.comision) || 0;
      const montoLocal = importe * (porcentaje / 100);
      pushComision(registros, {
        fecha: String(row.facturafecha).slice(0, 10),
        ano,
        mesIndice: fecha.getMonth(),
        montoLocal,
        bloque: "D",
        concepto: "comision_edificios_btl",
        pais,
        meta: { id: row.id, buildingIndex: idx, origen: "sales_btl" },
      });
    });
  });
  return registros;
};

/**
 * FASE 3 PASO 3 (criterio 1), punto 4 — filas-hoja que SUMAN varias de las 13 series de
 * comisión (3.1-3.5 + punto 2/F1 de este mismo encargo), para que cada fila del Bloque A/D
 * tenga UNA función de registros. Composición pura, mismo objeto de params para todas (cada
 * función interna destructura solo lo que necesita) — ninguna lógica nueva (10.1).
 */
export const registrosCostoIntermediacion = (p) => [
  ...registrosComisionAgenciaVentas(p),
  ...registrosComisionAgenciaContratos(p),
  ...registrosComisionBrokerVentas(p),
  ...registrosComisionBrokerContratos(p),
  ...registrosComisionTakeVentas(p),
  ...registrosComisionApxVentas(p),
  ...registrosComisionApxContratos(p),
];

export const registrosComisionEdificiosA = (p) => [
  ...registrosComisionEdificiosVentas(p),
  ...registrosComisionEdificiosContratos(p),
  ...registrosComisionEdificiosFijas(p),
];

export const registrosComisionComercialA = (p) => [
  ...registrosComisionComercialVentas(p),
  ...registrosComisionComercialContratos(p),
];
